import { useState } from 'react';
import { IconButton, Input, Select } from '@servicecompressionllc/sc-design-system';

import type { PickerVariable } from '../definitionEdit.js';
import { ChevronDown } from 'lucide-react';

interface Props {
  kind?: PickerVariable['kind'];
  value: string;
  values?: string[];
  onChange: (value: string) => void;
}

/** Predefined values guide equality conditions without restricting incoming event values. */
export const ConditionValue = ({ value, values, kind, onChange }: Props) => {
  const [custom, setCustom] = useState(false);
  const boolean = kind === 'boolean';
  const choices = boolean ? ['true', 'false'] : [...new Set(values ?? [])];
  const hasChoices = choices.length > 0;
  const options = choices.map((choice) => ({
    value: choice,
    label: boolean ? (choice === 'true' ? 'Yes' : 'No') : choice,
  }));
  if (value && !choices.includes(value)) options.push({ value, label: `${value} (custom)` });

  let customOption = '__custom_value__';
  while (options.some((option) => option.value === customOption)) customOption += '_';

  return (
    <div className="min-w-0">
      {hasChoices && !custom ? (
        <Select
          aria-label="Value"
          options={
            boolean
              ? options
              : [...options, { value: customOption, label: 'Enter a custom value…' }]
          }
          value={value ? [value] : []}
          placeholder="Choose a value"
          onChange={(selected) => {
            if (selected[0] === customOption) setCustom(true);
            else onChange(selected[0] ?? '');
          }}
        />
      ) : (
        <div className="flex min-w-0 items-center gap-1">
          <Input
            aria-label="Value"
            type={kind === 'number' ? 'number' : 'text'}
            step={kind === 'number' ? 'any' : undefined}
            className="min-w-0 flex-1"
            placeholder="Enter a value"
            autoFocus={custom}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          {hasChoices && (
            <IconButton
              type="button"
              aria-label="Choose a predefined value"
              title="Choose a predefined value"
              size="sm"
              onClick={() => setCustom(false)}
            >
              <ChevronDown />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
};
